import { create } from "zustand";
import { persist } from "zustand/middleware";
import { normalize } from "../search/useUniversalSearch.js";

/**
 * Notification settings (Section 12 steps 6 & 8), persisted on-device:
 *
 * - `enabled` — the opt-in switch; firing also requires the browser's
 *   Notification permission to be granted (requested from the Settings
 *   toggle, a user gesture).
 * - `trackedTitles` — titles the user asked to be alerted about (title
 *   tracking, §8.4). Keyed by normalized title, TTL'd (30 days, refreshed
 *   on re-track) and swept when expired.
 * - `fired` — dedupe keys of already-fired notifications (keyed by
 *   channel+start, so the same program never double-alerts), swept after a
 *   day. Persisted so a reload doesn't re-fire a whole minute of alerts.
 */
export interface TrackedTitle {
  title: string;
  addedAt: string;
  expiresAt: string;
}

const TITLE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
/** Fired entries older than this are swept — well past any alert window. */
const FIRED_TTL_MS = 26 * 60 * 60 * 1000;
/** How many alerts the in-app inbox keeps (newest first). */
const INBOX_CAP = 50;
/** Inbox alerts older than a day are swept — stale "starting soon" notices. */
const INBOX_TTL_MS = 24 * 60 * 60 * 1000;

export function trackedTitleKey(title: string): string {
  return normalize(title);
}

/**
 * In-app alert shown in the topbar bell — recorded regardless of browser
 * Notification permission, so TV users see alerts even when the browser can't
 * (or won't) show OS notifications.
 */
export interface InboxAlert {
  /** = the same dedupe key as `fired`, so a re-scan never duplicates. */
  id: string;
  kind: "starting-soon" | "tracked-title";
  title: string;
  channelName: string;
  startsAt: string;
  message: string;
  /** When the alert was recorded (ISO). */
  at: string;
  read: boolean;
}

export interface NotificationsStore {
  enabled: boolean;
  trackedTitles: Record<string, TrackedTitle>;
  fired: Record<string, string>;
  inbox: InboxAlert[];
  setEnabled: (enabled: boolean) => void;
  /** Request browser permission (must be called from a user gesture). */
  requestPermission: () => Promise<NotificationPermission | "unsupported">;
  addTrackedTitle: (title: string) => void;
  removeTrackedTitle: (title: string) => void;
  isTracked: (title: string) => boolean;
  trackedList: () => TrackedTitle[];
  markFired: (key: string) => void;
  isFired: (key: string) => boolean;
  /** Record an in-app alert (idempotent by id, capped, newest first). */
  pushAlert: (alert: Omit<InboxAlert, "at" | "read">) => void;
  /** Mark one alert read (or every alert when id is omitted). */
  markInboxRead: (id?: string) => void;
  dismissInbox: (id: string) => void;
  clearInbox: () => void;
  sweepExpired: (now: number) => void;
}

export const useNotifications = create<NotificationsStore>()(
  persist(
    (set, get) => ({
      enabled: false,
      trackedTitles: {},
      fired: {},
      inbox: [],

      setEnabled: (enabled) => set({ enabled }),

      requestPermission: async () => {
        if (typeof Notification === "undefined") return "unsupported";
        if (Notification.permission === "granted") return "granted";
        if (Notification.permission === "denied") return "denied";
        try {
          return await Notification.requestPermission();
        } catch {
          return "denied";
        }
      },

      addTrackedTitle: (title) => {
        const trimmed = title.trim();
        if (!trimmed) return;
        const key = trackedTitleKey(trimmed);
        const now = new Date().toISOString();
        set({
          trackedTitles: {
            ...get().trackedTitles,
            [key]: {
              title: trimmed,
              addedAt: now,
              expiresAt: new Date(Date.now() + TITLE_TTL_MS).toISOString(),
            },
          },
        });
      },

      removeTrackedTitle: (title) => {
        const key = trackedTitleKey(title);
        const next = { ...get().trackedTitles };
        delete next[key];
        set({ trackedTitles: next });
      },

      isTracked: (title) => trackedTitleKey(title) in get().trackedTitles,

      trackedList: () =>
        Object.values(get().trackedTitles).sort((a, b) => a.title.localeCompare(b.title)),

      markFired: (key) => {
        if (key in get().fired) return;
        set({ fired: { ...get().fired, [key]: new Date().toISOString() } });
      },

      isFired: (key) => key in get().fired,

      pushAlert: (alert) => {
        const existing = get().inbox.some((a) => a.id === alert.id);
        if (existing) return;
        const entry: InboxAlert = { ...alert, at: new Date().toISOString(), read: false };
        set({ inbox: [entry, ...get().inbox].slice(0, INBOX_CAP) });
      },

      markInboxRead: (id) => {
        if (id) {
          set({
            inbox: get().inbox.map((a) => (a.id === id ? { ...a, read: true } : a)),
          });
        } else {
          set({ inbox: get().inbox.map((a) => ({ ...a, read: true })) });
        }
      },

      dismissInbox: (id) => {
        set({ inbox: get().inbox.filter((a) => a.id !== id) });
      },

      clearInbox: () => set({ inbox: [] }),

      sweepExpired: (now) => {
        const tracked = { ...get().trackedTitles };
        let trackedChanged = false;
        for (const [key, entry] of Object.entries(tracked)) {
          if (new Date(entry.expiresAt).getTime() <= now) {
            delete tracked[key];
            trackedChanged = true;
          }
        }
        const fired = { ...get().fired };
        let firedChanged = false;
        for (const [key, at] of Object.entries(fired)) {
          if (now - new Date(at).getTime() > FIRED_TTL_MS) {
            delete fired[key];
            firedChanged = true;
          }
        }
        const inbox = get().inbox.filter((a) => now - new Date(a.at).getTime() <= INBOX_TTL_MS);
        const inboxChanged = inbox.length !== get().inbox.length;
        if (trackedChanged || firedChanged || inboxChanged) {
          set({ trackedTitles: tracked, fired, inbox });
        }
      },
    }),
    { name: "slique-notifications" },
  ),
);
